import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Department,
  ExpenseReportAnalyticGroup,
  ExpenseStatus,
  GraphAnalyticInterval,
  useAdminReportGraphMetricsQuery,
  useAdminUpsertDepartmentMutation,
  useOrganizationUsersQuery,
} from "@common/utils/graphql";
import { useMemo, useState } from "react";
import { InviteExistingUserContent } from "./InviteExistingUserContent";
import { PieChart } from "@common/components/Chart/PieChart";
import { CreateOrEditDepartmentContent } from "./CreateOrEditDepartmentContent";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DepartmentFormSchema } from "../forms/Forms";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/use-toast";
import { ConfirmationDialog } from "@common/components/ConfirmationDialog/ConfirmationDialog";
import {
  AttributeKeyValueCell,
  SheetTabsContainer,
  SheetTabsContent,
  SheetTabsList,
} from "@common/components";
import { Body } from "@hypercard/web-components";
import { formatDollars } from "@common/components/utils";

// Inclusive of today and 30 days ago
const now = new Date();
const endDay = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate(),
  23,
  59,
  59,
  999,
);
const startDay = new Date(now.getFullYear(), now.getMonth(), 0, 0, 0, 0, 0);

interface Props {
  department: Department;
  refetchDepartments: () => void;
  onClose: () => void;
}

const SheetTabs = [
  {
    name: "Overview",
    value: "overview",
  },
  {
    name: "Members",
    value: "members",
  },
];

export const ViewDepartmentContent = ({
  department,
  refetchDepartments,
  onClose,
}: Props) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [userSearchName, setUserSearchName] = useState("");
  const [isAddingUser, setIsAddingUser] = useState(false);

  const {
    data: adminDataRaw,
    loading: adminDataLoading,
    refetch: refetchAdminData,
  } = useOrganizationUsersQuery({
    variables: {
      departmentIds: [Number(department.id)],
      name: userSearchName,
      pageSize: 1000,
      offset: 0,
    },
  });
  const [upsertDepartment, { loading: isDeleting }] =
    useAdminUpsertDepartmentMutation();

  const { data: expenseSummaryDataRaw } = useAdminReportGraphMetricsQuery({
    variables: {
      groupedBy: ExpenseReportAnalyticGroup.Category,
      interval: GraphAnalyticInterval.Daily,
      filters: {
        transactedAfter: startDay,
        transactedBefore: endDay,
        expenseStatus: [ExpenseStatus.Approved],
        departmentId: [department.id],
      },
    },
  });

  const form = useForm<z.infer<typeof DepartmentFormSchema>>({
    resolver: zodResolver(DepartmentFormSchema),
    defaultValues: {
      name: department.name,
    },
  });

  const categorySummary = useMemo(() => {
    if (
      expenseSummaryDataRaw?.adminReportGraphMetrics.__typename !==
      "QueryAdminReportGraphMetricsSuccess"
    ) {
      return undefined;
    }
    const metrics = expenseSummaryDataRaw.adminReportGraphMetrics.data.metrics;
    const totalSpend = metrics.reduce((acc, curr) => acc + curr.totalValue, 0);
    // TODO: use top 5 categories
    return {
      totalSpend,
      top5Categories: metrics,
    };
  }, [expenseSummaryDataRaw]);

  const usersInDepartment =
    adminDataRaw?.organizationUsers?.__typename !==
    "QueryOrganizationUsersSuccess"
      ? []
      : adminDataRaw.organizationUsers.data.users;

  const deleteDepartment = async () => {
    let errorToastDescription = undefined;
    try {
      if (usersInDepartment.length > 0) {
        errorToastDescription = `Cannot delete deparment ${department.name} as it still has members`;
        throw new Error();
      }

      const { data } = await upsertDepartment({
        variables: {
          isShown: false,
          name: department.name,
          uuid: department.uuid,
        },
      });
      if (
        data?.adminUpsertDepartment.__typename !==
        "MutationAdminUpsertDepartmentSuccess"
      ) {
        throw new Error();
      }
      onClose();
    } catch (e) {
      toast({
        title: `Failed to delete department${errorToastDescription ? "" : ", please try again"}`,
        description: errorToastDescription,
        variant: "destructive",
      });
    }
  };

  const completeEditing = () => {
    setIsEditing(false);
    refetchDepartments();
  };

  if (isAddingUser) {
    return (
      <InviteExistingUserContent
        onBack={() => {
          setIsAddingUser(false);
          refetchAdminData();
        }}
        department={department}
      />
    );
  }

  if (isEditing) {
    return (
      <CreateOrEditDepartmentContent
        form={form}
        mode="edit"
        department={department}
        onBack={completeEditing}
      />
    );
  }

  const header = (
    <SheetHeader>
      <SheetTitle>{department.name}</SheetTitle>
    </SheetHeader>
  );

  return (
    <>
      {header}
      <SheetTabsContainer defaultValue="overview">
        <SheetTabsList tabs={SheetTabs} />

        {/* Overview Tab */}
        <SheetTabsContent value="overview" className="space-y-4">
          <AttributeKeyValueCell
            keyName="Members"
            value={usersInDepartment.length}
          />
          <div>
            <Body size="sm" weight="regular" className="text-white/70">
              This Month's Spend
            </Body>
            <Body size="2xl" weight="regular" className="mt-3">
              {formatDollars(categorySummary?.totalSpend ?? 0)}
            </Body>
            {categorySummary && categorySummary.totalSpend > 0 && (
              <div className="h-100 flex grow flex-row justify-between pt-[20px]">
                <PieChart
                  data={categorySummary.top5Categories.map((e) => ({
                    name: e.groupedEntityName,
                    amount: e.totalValue,
                  }))}
                />
              </div>
            )}
          </div>
          <div className="min-h-8 flex-1" />
          <ConfirmationDialog
            title="Delete Department"
            description={`Are you sure you want to delete this department?\nThis action cannot be undone.`}
            onConfirm={deleteDepartment}
            confirmText="Delete"
            asChild
          >
            <Button variant="destructive" disabled={isDeleting}>
              {isDeleting ? "Deleting Department" : "Delete Department"}
            </Button>
          </ConfirmationDialog>
          <Button
            className="mt-4"
            disabled={isDeleting}
            onClick={() => {
              setIsEditing(true);
            }}
          >
            Edit Department
          </Button>
        </SheetTabsContent>

        {/* Members Tab */}
        <SheetTabsContent value="members">
          <div>
            <Input
              className="mb-5 w-full"
              type="search"
              placeholder="Search"
              value={userSearchName}
              onChange={(e) => setUserSearchName(e.target.value)}
            />
            {adminDataLoading ? (
              <div className="text-center text-slate">Loading</div>
            ) : usersInDepartment.length === 0 ? (
              <div className="text-slate">No users found</div>
            ) : (
              usersInDepartment.map((user) => {
                return (
                  <div className="flex justify-between pb-4" key={user.userId}>
                    <div>
                      <div>{user.displayName}</div>
                      <div className="text-slate">
                        {user.employee?.workEmail}
                      </div>
                    </div>
                    <div className="flex items-center justify-center">
                      {user.employeeDisplayRole}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="min-h-8 flex-1" />
          <Button onClick={() => setIsAddingUser(true)}>Add User</Button>
        </SheetTabsContent>
      </SheetTabsContainer>
    </>
  );
};
